"use client"

import { useId, useMemo, useRef, useState, type ReactNode } from "react"
import { LexicalText, type DefaultHouseholdNameTemplates } from "@/schema"
import { pluralize } from "@/utils/text-helpers"
import { Button, Label, Switch } from "@ui/shad"
import { Braces } from "lucide-react"

import { SimpleFormDialog } from "@/components/dialogs"
import { Editor, EditorRefHandle } from "@/components/lexical"
import { PlaceholderData, registerPlaceholders } from "@/components/lexical/placeholders"
import DecoratorArrowNavigationPlugin from "@/components/lexical/plugins/decorator-arrow-navigation"
import PlaceholderPlugin, {
  INSERT_PLACEHOLDER_COMMAND,
  PlaceholderPickerPopover,
} from "@/components/lexical/plugins/placeholder"

const RENAME_PLACEHOLDERS: PlaceholderData[] = [
  {
    id: "rename_hoh_preferred_name",
    label: "HOH preferred name",
    value: "{{ hoh.preferred_name }}",
    category: "Head of household",
  },
  {
    id: "rename_hoh_first_name",
    label: "HOH first name",
    value: "{{ hoh.first_name }}",
    category: "Head of household",
  },
  {
    id: "rename_hoh_first_name_initial",
    label: "HOH first name initial",
    value: "{{ hoh.first_name_initial }}",
    category: "Head of household",
  },
  { id: "rename_hoh_last_name", label: "HOH last name", value: "{{ hoh.last_name }}", category: "Head of household" },
  { id: "rename_hoh_full_name", label: "HOH full name", value: "{{ hoh.full_name }}", category: "Head of household" },
  {
    id: "rename_spouse_preferred_name",
    label: "Spouse preferred name",
    value: "{{ spouse.preferred_name }}",
    category: "Spouse/partner",
  },
  {
    id: "rename_spouse_first_name",
    label: "Spouse first name",
    value: "{{ spouse.first_name }}",
    category: "Spouse/partner",
  },
  {
    id: "rename_spouse_first_name_initial",
    label: "Spouse first name initial",
    value: "{{ spouse.first_name_initial }}",
    category: "Spouse/partner",
  },
  {
    id: "rename_spouse_last_name",
    label: "Spouse last name",
    value: "{{ spouse.last_name }}",
    category: "Spouse/partner",
  },
  {
    id: "rename_spouse_full_name",
    label: "Spouse full name",
    value: "{{ spouse.full_name }}",
    category: "Spouse/partner",
  },
]

const HOH_ONLY_PLACEHOLDERS = RENAME_PLACEHOLDERS.filter((p) => p.category === "Head of household")

registerPlaceholders(RENAME_PLACEHOLDERS)

const EDITOR_STYLES: React.CSSProperties & Record<`--${string}`, string> = {
  "--lexical-editor-font-size": "14px",
  "--lexical-editor-min-height": "0",
  "--lexical-editor-padding-top": "6px",
  "--lexical-editor-padding-bottom": "6px",
  "--lexical-editor-padding-x": "10px",
  "--lexical-editor-placeholder-top": "6px",
  "--lexical-editor-placeholder-left": "10px",
}

const EXAMPLE_HOH = {
  first_name: "Jim",
  preferred_name: "Jimmy",
  first_name_initial: "J",
  last_name: "Halpert",
  full_name: "Jim Halpert",
}
const EXAMPLE_SAME_LAST_NAME_SPOUSE = {
  first_name: "Pam",
  preferred_name: "Pammy",
  first_name_initial: "P",
  last_name: "Halpert",
  full_name: "Pam Halpert",
}
const EXAMPLE_DIFFERENT_LAST_NAME_SPOUSE = {
  first_name: "Pam",
  preferred_name: "Pammy",
  first_name_initial: "P",
  last_name: "Beesly",
  full_name: "Pam Beesly",
}

export const DEFAULT_HOUSEHOLD_NAME_TEMPLATES: DefaultHouseholdNameTemplates = {
  include_spouse: true,
  no_spouse_template: "{{ hoh.first_name }} {{ hoh.last_name }}",
  same_last_name_template: "{{ hoh.first_name }} and {{ spouse.first_name }} {{ hoh.last_name }}",
  different_last_name_template: "{{ hoh.full_name }} and {{ spouse.full_name }}",
}

export type HouseholdNamePreviewType = "no_spouse" | "same_last_name" | "different_last_name"

export function renderHouseholdNamePreview(template: string, previewType: HouseholdNamePreviewType): string {
  const hoh = EXAMPLE_HOH
  const spouse =
    previewType === "same_last_name"
      ? EXAMPLE_SAME_LAST_NAME_SPOUSE
      : previewType === "different_last_name"
        ? EXAMPLE_DIFFERENT_LAST_NAME_SPOUSE
        : null

  return template
    .replace(/\{\{\s*hoh\.first_name_initial\s*\}\}/g, hoh.first_name_initial)
    .replace(/\{\{\s*hoh\.first_name\s*\}\}/g, hoh.first_name)
    .replace(/\{\{\s*hoh\.preferred_name\s*\}\}/g, hoh.preferred_name)
    .replace(/\{\{\s*hoh\.last_name\s*\}\}/g, hoh.last_name)
    .replace(/\{\{\s*hoh\.full_name\s*\}\}/g, hoh.full_name)
    .replace(/\{\{\s*spouse\.first_name_initial\s*\}\}/g, spouse?.first_name_initial ?? "")
    .replace(/\{\{\s*spouse\.first_name\s*\}\}/g, spouse?.first_name ?? "")
    .replace(/\{\{\s*spouse\.preferred_name\s*\}\}/g, spouse?.preferred_name ?? "")
    .replace(/\{\{\s*spouse\.last_name\s*\}\}/g, spouse?.last_name ?? "")
    .replace(/\{\{\s*spouse\.full_name\s*\}\}/g, spouse?.full_name ?? "")
    .replace(/\s+/g, " ")
    .trim()
}

export type BulkRenameTemplates = DefaultHouseholdNameTemplates

interface BulkRenameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (templates: BulkRenameTemplates) => void
  isSubmitting: boolean
  selectedCount: number
  entityLabel: "client" | "prospect"
}

interface HouseholdNameTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (templates: DefaultHouseholdNameTemplates) => void
  isSubmitting: boolean
  title: string
  description: ReactNode
  confirmText: string
  initialTemplates?: DefaultHouseholdNameTemplates
}

function TemplateField({
  id,
  label,
  defaultValue,
  onChange,
  placeholders,
  previewType,
}: {
  id: string
  label: string
  defaultValue: string
  onChange: (value: string) => void
  placeholders: PlaceholderData[]
  previewType: HouseholdNamePreviewType
}) {
  const editorRef = useRef<EditorRefHandle>(null)

  const initialContent = useMemo(
    () => ({ markdown: defaultValue, preferred_format: "markdown" as const }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const [currentValue, setCurrentValue] = useState(defaultValue)

  const handleChange = (newContent: LexicalText) => {
    // Remove Lexical's U+200B empty-editor padding.
    const text = (newContent.markdown ?? "").replace(/\n/g, " ").replace(/\u200B/g, "")
    setCurrentValue(text)
    onChange(text)
  }

  const handleSelectPlaceholder = (placeholder: PlaceholderData) => {
    const editor = editorRef.current?.getEditor()
    if (editor) {
      editor.focus()
      editor.dispatchCommand(INSERT_PLACEHOLDER_COMMAND, placeholder)
    }
  }

  const preview = renderHouseholdNamePreview(currentValue, previewType)

  return (
    <div>
      <Label htmlFor={id} className="text-sm font-normal">
        {label}
      </Label>
      <div className="border-input mt-1 rounded-md border">
        <Editor
          ref={editorRef}
          onChange={handleChange}
          onEnter={() => {}}
          initialContent={initialContent}
          startingPlaceholder="Type { to insert a variable"
          isPlainText={true}
          styleOverrides={EDITOR_STYLES}
          placeholders={placeholders}
          proseClasses=""
        >
          <PlaceholderPlugin placeholders={placeholders} />
          <DecoratorArrowNavigationPlugin />
        </Editor>
        <div className="mx-2 flex items-center border-t py-1">
          <PlaceholderPickerPopover placeholders={placeholders} onSelect={handleSelectPlaceholder}>
            <Button type="button" variant="ghost" size="xs">
              <Braces />
              Insert variable
            </Button>
          </PlaceholderPickerPopover>
        </div>
      </div>
      {preview && (
        <div className="bg-muted mt-1 rounded-md px-3 py-2">
          <p className="text-muted-foreground text-xs">
            Example: <span className="text-foreground font-medium">{preview}</span>
          </p>
        </div>
      )}
    </div>
  )
}

export function BulkRenameDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  selectedCount,
  entityLabel,
}: BulkRenameDialogProps) {
  return (
    <HouseholdNameTemplateDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      isSubmitting={isSubmitting}
      title={`Rename ${pluralize(selectedCount, entityLabel)}`}
      description={
        <p>
          Define a naming template. Type <kbd className="bg-muted rounded px-1 py-0.5 font-mono text-xs">{"{"}</kbd> to
          see available variables.
        </p>
      }
      confirmText={`Rename ${pluralize(selectedCount, "item")}`}
    />
  )
}

export function HouseholdNameTemplateDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  title,
  description,
  confirmText,
  initialTemplates = DEFAULT_HOUSEHOLD_NAME_TEMPLATES,
}: HouseholdNameTemplateDialogProps) {
  if (!open) return null

  return (
    <HouseholdNameTemplateDialogInner
      {...{
        open,
        onOpenChange,
        onConfirm,
        isSubmitting,
        title,
        description,
        confirmText,
        initialTemplates,
      }}
    />
  )
}

function HouseholdNameTemplateDialogInner({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  title,
  description,
  confirmText,
  initialTemplates,
}: Required<HouseholdNameTemplateDialogProps>) {
  const formId = useId()
  const [includeSpouse, setIncludeSpouse] = useState(initialTemplates.include_spouse)
  const [noSpouseTemplate, setNoSpouseTemplate] = useState(initialTemplates.no_spouse_template)
  const [sameLastNameTemplate, setSameLastNameTemplate] = useState(initialTemplates.same_last_name_template)
  const [differentLastNameTemplate, setDifferentLastNameTemplate] = useState(
    initialTemplates.different_last_name_template
  )

  function handleConfirm() {
    onConfirm({
      include_spouse: includeSpouse,
      no_spouse_template: noSpouseTemplate,
      same_last_name_template: includeSpouse ? sameLastNameTemplate : noSpouseTemplate,
      different_last_name_template: includeSpouse ? differentLastNameTemplate : noSpouseTemplate,
    })
  }

  return (
    <SimpleFormDialog
      title={title}
      open={open}
      onOpenChange={onOpenChange}
      action={{ label: confirmText, form: formId, disabled: isSubmitting, pending: isSubmitting }}
      cancel={{ onClick: () => onOpenChange(false), disabled: isSubmitting }}
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (!isSubmitting) handleConfirm()
        }}
      >
        {description}
        <div className="flex items-center gap-2">
          <Switch checked={includeSpouse} onCheckedChange={setIncludeSpouse} id="include-spouse" />
          <Label htmlFor="include-spouse" className="text-sm font-normal">
            Include spouse/partner
          </Label>
        </div>
        {includeSpouse ? (
          <div className="space-y-3">
            <TemplateField
              id="same-last-name"
              label="Same last name"
              defaultValue={sameLastNameTemplate}
              onChange={setSameLastNameTemplate}
              placeholders={RENAME_PLACEHOLDERS}
              previewType="same_last_name"
            />
            <TemplateField
              id="different-last-name"
              label="Different last names"
              defaultValue={differentLastNameTemplate}
              onChange={setDifferentLastNameTemplate}
              placeholders={RENAME_PLACEHOLDERS}
              previewType="different_last_name"
            />
            <TemplateField
              id="no-spouse"
              label="No spouse/partner"
              defaultValue={noSpouseTemplate}
              onChange={setNoSpouseTemplate}
              placeholders={HOH_ONLY_PLACEHOLDERS}
              previewType="no_spouse"
            />
          </div>
        ) : (
          <TemplateField
            id="template"
            label="Template"
            defaultValue={noSpouseTemplate}
            onChange={setNoSpouseTemplate}
            placeholders={HOH_ONLY_PLACEHOLDERS}
            previewType="no_spouse"
          />
        )}
      </form>
    </SimpleFormDialog>
  )
}
